import { type ColorMode } from "./scanlib";

// Capture helpers. Crop/deskew now happens ON-DEVICE via Scanic (see detect.ts) between
// snapshotting the frame and finishing it — so this module just snapshots to a canvas and then
// finishes a (possibly already-cropped) canvas → colour mode → downscale → JPEG.

export const MAX_PAGES = 10;
export const MAX_EDGE = 1600;
// Per-mode JPEG quality. Document text is high-frequency, so even grey/bw stay large at 0.8 — a
// lower quality cuts file size a lot with little legible loss; colour keeps a touch more for photos.
const QUALITY: Record<ColorMode, number> = { color: 0.72, grayscale: 0.4, bw: 0.45 };
// Per-mode output resolution (long edge). Grey/bw go smaller than colour — Gery wants grey < 200 KB,
// and document text stays legible at ~1200 px while the file roughly halves vs 1600.
const EDGE: Record<ColorMode, number> = { color: MAX_EDGE, grayscale: 1200, bw: 1400 };

export async function openCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: "environment" }, width: { ideal: 2560 }, height: { ideal: 1920 } },
    audio: false,
  });
}

/** Load a photo File (native OS camera) into a canvas, capped at maxEdge on the long side.
 *  NEVER load the raw ~12MP camera frame into a canvas: iOS silently renders blank canvases
 *  under memory pressure (the old black-frame bug), which made detection fail on-device while
 *  the same photo detected fine off-device. The upload is ≤1600px anyway, so nothing is lost. */
export async function fileToCanvas(file: File, maxEdge: number = MAX_EDGE): Promise<HTMLCanvasElement> {
  const img = await loadImage(file);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const s = Math.min(1, maxEdge / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w * s));
  c.height = Math.max(1, Math.round(h * s));
  c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
  return c;
}

/** Finish a (possibly already cropped/deskewed) canvas → colour mode → downscale → JPEG blob. */
export function finishCanvas(frame: HTMLCanvasElement, mode: ColorMode): Promise<Blob> {
  const canvas = applyColorMode(frame, mode);
  return canvasToBlob(downscale(canvas, EDGE[mode]), QUALITY[mode]);
}

// Lightweight colour modes (no OpenCV). grayscale = luma; bw = global-mean threshold biased
// toward white (good for documents). Both also shrink the JPEG vs full colour.
function applyColorMode(canvas: HTMLCanvasElement, mode: ColorMode): HTMLCanvasElement {
  if (mode === "color") return canvas;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  if (mode === "grayscale") {
    for (let i = 0; i < d.length; i += 4) {
      const g = (d[i]! * 0.299 + d[i + 1]! * 0.587 + d[i + 2]! * 0.114) | 0;
      d[i] = d[i + 1] = d[i + 2] = g;
    }
  } else {
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += d[i]! * 0.299 + d[i + 1]! * 0.587 + d[i + 2]! * 0.114;
    const thr = (sum / (d.length / 4)) * 0.88;
    for (let i = 0; i < d.length; i += 4) {
      const v = d[i]! * 0.299 + d[i + 1]! * 0.587 + d[i + 2]! * 0.114 < thr ? 0 : 255;
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Re-encode a stored (colour) page blob into the chosen colour mode. Lets the Colour/Grey/B&W
 *  toggle act on the WHOLE document at send time — pages are captured/kept in colour, then all get
 *  the final mode together (no more mixed pages when you switch mode mid-scan). */
export async function recolorBlob(blob: Blob, mode: ColorMode): Promise<Blob> {
  if (mode === "color") return blob;
  const img = await loadImage(blob);
  const c = document.createElement("canvas");
  c.width = img.naturalWidth || img.width;
  c.height = img.naturalHeight || img.height;
  c.getContext("2d", { willReadFrequently: true })!.drawImage(img, 0, 0);
  return canvasToBlob(applyColorMode(downscale(c, EDGE[mode]), mode), QUALITY[mode]);
}

function loadImage(src: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(src);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });
}

function downscale(src: HTMLCanvasElement, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(src.width, src.height));
  if (scale >= 1) return src;
  const out = document.createElement("canvas");
  out.width = Math.round(src.width * scale);
  out.height = Math.round(src.height * scale);
  out.getContext("2d")!.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob failed"))), "image/jpeg", quality),
  );
}
